import { db } from './firebase';
import {
  collection, doc, setDoc, getDoc, getDocs, updateDoc, deleteDoc, onSnapshot, serverTimestamp
} from 'firebase/firestore';
import debounce from 'lodash.debounce';

// Types
export interface Column {
  id: string;
  title: string;
  cardIds: string[];
}
export interface CardType {
  id: string;
  type: 'note' | 'resource' | 'vuln' | 'tool' | 'payload';
  title: string;
  content: any;
  tags?: string[];
  createdAt: number;
  updatedAt: number;
}
export interface Board {
  id: string;
  name: string;
  columns: Column[];
  cards: { [cardId: string]: CardType };
  createdAt: number;
  updatedAt: number;
  extra?: {
    cardPositions?: { [cardId: string]: { x: number; y: number } };
  };
}

const getBoardsCollection = (userId: string) => collection(db, 'users', userId, 'huntersBoards');
const getBoardDoc = (userId: string, boardId: string) => doc(db, 'users', userId, 'huntersBoards', boardId);

// Helper to call the new API, fallback to Firestore if fails
async function apiFetch(path: string, options?: RequestInit) {
  try {
    const res = await fetch(path, options);
    if (!res.ok) throw new Error('API error');
    return await res.json();
  } catch (e) {
    return null;
  }
}

// Cache for loaded boards to avoid repeated Firestore calls
const boardCache = new Map<string, { board: Board; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Load all boards once and cache them
export async function loadAllBoards(userId: string): Promise<Board[]> {
  const cacheKey = `all-${userId}`;
  const cached = boardCache.get(cacheKey);
  
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.board as any; // Return cached boards
  }

  const apiRes = await apiFetch(`/api/hunters-board?userId=${encodeURIComponent(userId)}`);
  if (apiRes && Array.isArray(apiRes)) {
    boardCache.set(cacheKey, { board: apiRes as any, timestamp: Date.now() });
    return apiRes;
  }
  
  // fallback to Firestore
  const snap = await getDocs(getBoardsCollection(userId));
  const boards = snap.docs.map(doc => doc.data() as Board);
  
  // Cache the result
  boardCache.set(cacheKey, { board: boards as any, timestamp: Date.now() });
  return boards;
}

// Load a single board and cache it
export async function loadBoard(userId: string, boardId: string): Promise<Board | null> {
  const cacheKey = `${userId}-${boardId}`;
  const cached = boardCache.get(cacheKey);
  
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.board;
  }

  const apiRes = await apiFetch(`/api/hunters-board?userId=${encodeURIComponent(userId)}&boardId=${encodeURIComponent(boardId)}`);
  if (apiRes && apiRes.id) {
    boardCache.set(cacheKey, { board: apiRes as Board, timestamp: Date.now() });
    return apiRes as Board;
  }
  
  // fallback to Firestore
  const snap = await getDoc(getBoardDoc(userId, boardId));
  if (!snap.exists()) return null;
  
  const board = snap.data() as Board;
  boardCache.set(cacheKey, { board, timestamp: Date.now() });
  return board;
}

// Clear cache for a specific board or all boards
export function clearBoardCache(userId?: string, boardId?: string) {
  if (userId && boardId) {
    boardCache.delete(`${userId}-${boardId}`);
  } else if (userId) {
    // Clear all boards for this user
    for (const key of boardCache.keys()) {
      if (key.startsWith(`${userId}-`) || key === `all-${userId}`) {
        boardCache.delete(key);
      }
    }
  } else {
    // Clear all cache
    boardCache.clear();
  }
}

// Significant actions that should trigger immediate save
export async function saveBoard(userId: string, board: Board, forceSave: boolean = false): Promise<void> {
  // Update cache
  const cacheKey = `${userId}-${board.id}`;
  boardCache.set(cacheKey, { board, timestamp: Date.now() });
  
  // Clear the "all boards" cache since we modified a board
  boardCache.delete(`all-${userId}`);

  const apiRes = await apiFetch(`/api/hunters-board?userId=${encodeURIComponent(userId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(board)
  });
  if (apiRes && apiRes.success) return;
  
  // fallback to Firestore
  await setDoc(getBoardDoc(userId, board.id), {
    ...board,
    updatedAt: Date.now(),
    createdAt: board.createdAt || Date.now(),
  });
}

export async function deleteBoard(userId: string, boardId: string): Promise<void> {
  // Clear cache
  clearBoardCache(userId, boardId);
  boardCache.delete(`all-${userId}`);

  const apiRes = await apiFetch(`/api/hunters-board?userId=${encodeURIComponent(userId)}&boardId=${encodeURIComponent(boardId)}`, {
    method: 'DELETE'
  });
  if (apiRes && apiRes.success) return;
  
  // fallback to Firestore
  await deleteDoc(getBoardDoc(userId, boardId));
}

// Debounced save for minor changes (like card positions, content edits)
export const debouncedSaveBoard = debounce(async (userId: string, board: Board) => {
  await saveBoard(userId, board, false);
}, 2000); // Increased debounce time to 2 seconds

// Legacy functions for backward compatibility
export async function getAllBoards(userId: string): Promise<Board[]> {
  return loadAllBoards(userId);
}

export async function getBoard(userId: string, boardId: string): Promise<Board | null> {
  return loadBoard(userId, boardId);
}

export function subscribeToBoards(userId: string, callback: (boards: Board[]) => void) {
  // Real-time updates still use Firestore for now
  // In the future, this could be replaced with WebSocket or polling
  return onSnapshot(getBoardsCollection(userId), snap => {
    const boards = snap.docs.map(doc => doc.data() as Board);
    // Update cache with real-time data
    boards.forEach(board => {
      const cacheKey = `${userId}-${board.id}`;
      boardCache.set(cacheKey, { board, timestamp: Date.now() });
    });
    callback(boards);
  });
} 